import { useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { categoryArticles } from '@/mocks/categoryData';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const article = useMemo(() => {
    for (const cat of Object.values(categoryArticles)) {
      const found = cat.find((a) => a.id === id);
      if (found) return found;
    }
    return null;
  }, [id]);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    const all = Object.values(categoryArticles).flat();
    return all.filter((a) => a.id !== article.id && a.category === article.category).slice(0, 3);
  }, [article]);

  const articleJsonLd = useMemo(() => {
    if (!article) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.summary,
      image: article.image,
      datePublished: article.date,
      author: {
        '@type': 'Organization',
        name: '开云足球',
        url: 'https://kaiyun-football.com/',
      },
      publisher: {
        '@type': 'Organization',
        name: '开云足球',
        url: 'https://kaiyun-football.com/',
      },
      isPartOf: {
        '@type': 'WebSite',
        name: '开云足球',
        url: 'https://kaiyun-football.com/',
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `https://kaiyun-football.com/article/${article.id}`,
      },
    };
  }, [article]);

  const breadcrumbJsonLd = useMemo(() => {
    if (!article) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: '首页',
          item: 'https://kaiyun-football.com/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: article.category,
          item: `https://kaiyun-football.com/category/live`,
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: article.title,
          item: `https://kaiyun-football.com/article/${article.id}`,
        },
      ],
    };
  }, [article]);

  if (!article) {
    return (
      <div className="w-full px-4 md:px-6 py-20 text-center">
        <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-2xl bg-background-100 mb-4">
          <i className="ri-file-close-line text-2xl text-foreground-400"></i>
        </div>
        <h2 className="font-heading text-xl font-bold text-foreground-900 mb-2">文章未找到</h2>
        <p className="text-foreground-500 text-sm mb-6">您访问的文章可能已被删除或链接不正确</p>
        <Link
          to="/"
          className="inline-flex items-center gap-1 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors"
        >
          <i className="ri-arrow-left-line"></i>
          返回首页
        </Link>
      </div>
    );
  }

  return (
    <div className="w-full">
      {articleJsonLd && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }} />
      )}
      {breadcrumbJsonLd && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }} />
      )}

      <section className="w-full py-10 md:py-14 px-4 md:px-6 bg-background-50">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-5">
            <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
              {article.category}
            </span>
            <span className="text-foreground-400 text-xs">{article.date}</span>
            <span className="text-foreground-400 text-xs flex items-center gap-1">
              <i className="ri-time-line"></i>
              {article.readTime}
            </span>
          </div>

          <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-6 leading-tight">
            {article.title}
          </h1>

          <div className="w-full h-48 md:h-80 rounded-xl overflow-hidden mb-8">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div
            className="prose prose-sm md:prose-base max-w-none prose-headings:font-heading prose-headings:text-foreground-900 prose-p:text-foreground-600 prose-p:leading-relaxed prose-a:text-primary-600 prose-a:no-underline hover:prose-a:text-primary-700"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />
        </div>
      </section>

      {relatedArticles.length > 0 && (
        <section className="w-full py-10 md:py-14 px-4 md:px-6 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
              相关推荐
            </h2>
            <div className="flex flex-col gap-4">
              {relatedArticles.map((related) => (
                <Link
                  key={related.id}
                  to={`/article/${related.id}`}
                  className="group flex gap-4 p-4 rounded-xl bg-background-50 hover:bg-background-200/70 transition-all duration-300"
                >
                  <div className="w-24 h-16 md:w-32 md:h-20 rounded-lg overflow-hidden flex-shrink-0">
                    <img
                      src={related.thumb}
                      alt={related.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors leading-snug mb-1 line-clamp-2">
                      {related.title}
                    </h3>
                    <p className="text-foreground-500 text-xs hidden sm:block line-clamp-1">
                      {related.summary}
                    </p>
                    <span className="text-foreground-400 text-xs mt-1 inline-block">
                      {related.date}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="w-full py-10 md:py-14 px-4 md:px-6 bg-background-50">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3">
            喜欢这篇文章？
          </h2>
          <p className="text-foreground-500 text-sm mb-6">
            订阅开云足球资讯，第一时间获取更多精彩文章和赛事分析
          </p>
          <Link
            to="/category/events"
            className="inline-flex items-center gap-1 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors"
          >
            探索更多内容
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>
    </div>
  );
}